import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router';
import { Layout, Menu, Tabs, Avatar, Dropdown, Message, Button } from '@arco-design/web-react';
import {
  IconDashboard,
  IconApps,
  IconMenuFold,
  IconMenuUnfold,
  IconPoweroff,
  IconSun,
  IconMoon,
  IconIdcard,
  IconLock,
  IconList,
  IconStorage,
  IconUserGroup,
  IconFile,
  IconCode,
  IconUser,
  IconMessage,
  IconStop,
  IconBook,
} from '@arco-design/web-react/icon';
import { useTheme } from '@pingo/shared';
import { authApi } from '@pingo/api';
import { useAuthStore } from '../stores/auth';
import { useUIStore } from '../stores/ui';
import { useTabStore } from '../stores/tabs';

const { Sider, Header, Content } = Layout;

// 菜单项配置
interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  children?: { key: string; icon: React.ReactNode; label: string }[];
}

const menuItems: MenuItem[] = [
  { key: '/dashboard', icon: <IconDashboard />, label: '仪表盘' },
  {
    key: 'app-mgmt',
    icon: <IconApps />,
    label: '应用管理',
    children: [
      { key: '/apps', icon: <IconList />, label: '应用列表' },
      { key: '/credentials', icon: <IconLock />, label: '凭证列表' },
    ],
  },
  {
    key: 'card-mgmt',
    icon: <IconIdcard />,
    label: '卡类管理',
    children: [
      { key: '/card-types', icon: <IconList />, label: '卡类列表' },
      { key: '/cards', icon: <IconStorage />, label: '卡密列表' },
      { key: '/card-usages', icon: <IconFile />, label: '使用记录' },
    ],
  },
  {
    key: 'agent-mgmt',
    icon: <IconUserGroup />,
    label: '代理商管理',
    children: [
      { key: '/agents', icon: <IconList />, label: '代理商列表' },
      { key: '/agent-purchases', icon: <IconFile />, label: '购卡记录' },
    ],
  },
  {
    key: 'user-mgmt',
    icon: <IconUser />,
    label: '用户管理',
    children: [
      { key: '/end-users', icon: <IconList />, label: '用户列表' },
      { key: '/end-users/online', icon: <IconUserGroup />, label: '在线用户' },
    ],
  },
  {
    key: 'remote-mgmt',
    icon: <IconCode />,
    label: '远程管理',
    children: [
      { key: '/remote-variables', icon: <IconList />, label: '变量列表' },
      { key: '/remote-functions', icon: <IconCode />, label: '函数列表' },
      { key: '/feature-quotas', icon: <IconStorage />, label: '功能配额' },
      { key: '/feature-quota-logs', icon: <IconFile />, label: '功能配额日志' },
      { key: '/timed-features', icon: <IconStorage />, label: '时段扣点' },
      { key: '/blacklist', icon: <IconStop />, label: '黑名单' },
    ],
  },
  {
    key: 'forward-mgmt',
    icon: <IconCode />,
    label: '转发管理',
    children: [
      { key: '/forwarding-features', icon: <IconCode />, label: '转发功能' },
      { key: '/forwarding-records', icon: <IconFile />, label: '转发记录' },
    ],
  },
  // 属性模板（顶级单项，无子菜单）
  { key: '/attribute-templates', icon: <IconBook />, label: '属性模板' },
  {
    key: 'ticket-mgmt',
    icon: <IconMessage />,
    label: '工单管理',
    children: [{ key: '/tickets', icon: <IconList />, label: '工单列表' }],
  },
  {
    key: 'log-mgmt',
    icon: <IconFile />,
    label: '日志管理',
    children: [
      { key: '/audit-logs', icon: <IconList />, label: '审计日志' },
      { key: '/client-logs', icon: <IconCode />, label: '客户端日志' },
      { key: '/payment-orders', icon: <IconFile />, label: '订单记录' },
    ],
  },
];

// 根据路径计算需要展开的 SubMenu
function getOpenKeys(pathname: string): string[] {
  const first = '/' + pathname.split('/')[1];
  if (first === '/apps' || first === '/credentials') return ['app-mgmt'];
  if (first === '/card-types' || first === '/cards' || first === '/card-usages')
    return ['card-mgmt'];
  if (first === '/agents' || first === '/agent-purchases') return ['agent-mgmt'];
  if (
    first === '/remote-variables' ||
    first === '/remote-functions' ||
    first === '/feature-quotas' ||
    first === '/feature-quota-logs' ||
    first === '/timed-features' ||
    first === '/blacklist'
  )
    return ['remote-mgmt'];
  if (first === '/forwarding-features' || first === '/forwarding-records') return ['forward-mgmt'];
  if (first === '/end-users') return ['user-mgmt'];
  if (first === '/tickets') return ['ticket-mgmt'];
  if (first === '/audit-logs' || first === '/client-logs' || first === '/payment-orders')
    return ['log-mgmt'];
  return [];
}

// 主布局（侧边栏 + 顶栏 + 标签栏 + 内容区）
export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth } = useAuthStore();
  const { siderCollapsed, toggleSider } = useUIStore();
  const { theme, toggleTheme } = useTheme();
  const { tabs, activeKey, addTab, removeTab, setActiveTab } = useTabStore();

  // 监听路由变化，自动添加/激活标签
  useEffect(() => {
    const pathname = location.pathname;
    // 忽略根路径（会被重定向到 /dashboard）
    if (pathname === '/') return;
    addTab(pathname);
  }, [location.pathname, addTab]);

  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    navigate(key);
  };

  const handleTabClose = (key: string) => {
    const nextPath = removeTab(key);
    if (nextPath) {
      navigate(nextPath);
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } finally {
      clearAuth();
      navigate('/login', { replace: true });
      Message.success('已退出登录');
    }
  };

  // 当前选中菜单项：优先精确匹配完整路径，否则取一级路径
  const menuKeys = menuItems.flatMap((item) =>
    item.children ? item.children.map((c) => c.key) : [item.key],
  );
  const currentPath =
    menuKeys.find((k) => location.pathname === k) || '/' + location.pathname.split('/')[1];
  const defaultOpenKeys = getOpenKeys(location.pathname);

  return (
    <Layout style={{ minHeight: '100vh', background: 'var(--color-bg-1)' }}>
      <Sider
        collapsed={siderCollapsed}
        collapsible
        trigger={null}
        breakpoint="lg"
        width={220}
        collapsedWidth={48}
        style={{ background: 'var(--color-bg-2)' }}
      >
        <div
          style={{
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: siderCollapsed ? 16 : 20,
            color: 'rgb(var(--primary-6))',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          {siderCollapsed ? 'P' : 'Pingo'}
        </div>
        <Menu
          selectedKeys={[currentPath]}
          defaultOpenKeys={defaultOpenKeys}
          onClickMenuItem={handleMenuClick}
          style={{ width: '100%' }}
        >
          {menuItems.map((item) =>
            item.children ? (
              <Menu.SubMenu
                key={item.key}
                title={
                  <span>
                    {item.icon} {item.label}
                  </span>
                }
              >
                {item.children.map((child) => (
                  <Menu.Item key={child.key}>
                    {child.icon} {child.label}
                  </Menu.Item>
                ))}
              </Menu.SubMenu>
            ) : (
              <Menu.Item key={item.key}>
                {item.icon}
                {item.label}
              </Menu.Item>
            ),
          )}
        </Menu>
      </Sider>

      <Layout>
        <Header
          style={{
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            background: 'var(--color-bg-2)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <span style={{ cursor: 'pointer', fontSize: 18 }} onClick={toggleSider}>
            {siderCollapsed ? <IconMenuUnfold /> : <IconMenuFold />}
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type="text"
              icon={theme === 'dark' ? <IconSun /> : <IconMoon />}
              onClick={toggleTheme}
              style={{ color: 'var(--color-text-2)', fontSize: 18 }}
            />
            <Dropdown
              droplist={
                <Menu
                  onClickMenuItem={(key) => {
                    if (key === 'logout') handleLogout();
                  }}
                >
                  <Menu.Item key="logout">
                    <IconPoweroff style={{ marginRight: 8 }} />
                    退出登录
                  </Menu.Item>
                </Menu>
              }
              position="br"
            >
              <span style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar size={28} style={{ backgroundColor: 'rgb(var(--primary-6))' }}>
                  {user?.username?.[0]?.toUpperCase() ?? 'U'}
                </Avatar>
                <span style={{ fontSize: 14, color: 'var(--color-text-1)' }}>
                  {user?.username ?? ''}
                </span>
              </span>
            </Dropdown>
          </div>
        </Header>

        <div
          style={{
            background: 'var(--color-bg-2)',
            borderBottom: '1px solid var(--color-border)',
            padding: '0 12px',
          }}
        >
          <Tabs
            activeTab={activeKey}
            editable
            type="card-gutter"
            size="small"
            onChange={handleTabChange}
            onDeleteTab={handleTabClose}
            style={{ marginBottom: 0 }}
          >
            {tabs.map((tab) => (
              <Tabs.TabPane key={tab.key} title={tab.title} closable={tab.closable} />
            ))}
          </Tabs>
        </div>

        <Content style={{ padding: 16 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
